import { motion } from 'framer-motion'
import { X } from 'lucide-react'

import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { getReactionGroupKey, renderReactionIcon } from '@/components/reactions/reaction-display'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { useAuth } from '@/contexts/auth-context'

import { ScrollArea } from '../ui/scroll-area'

// Generic reaction interface that all reaction types should extend
interface BaseReaction {
  id: number
  reactionType: string
  emojiData?: {
    unicode?: string
    shortcode?: string
    imageUrl?: string
    name?: string
  }
  user: {
    id: number
    firstName: string
    lastName: string
    avatar?: string | null
  }
  createdAt?: string
}

interface ReactionListDialogProps {
  isOpen: boolean
  onClose: () => void
  reactions: BaseReaction[]
  messageId?: number
  onRemoveReaction?: (messageId: number, reactionType: string, emojiData?: BaseReaction['emojiData']) => void
}

export function ReactionListDialog({
  isOpen,
  onClose,
  reactions,
  messageId,
  onRemoveReaction,
}: ReactionListDialogProps) {
  const { user } = useAuth()

  // Filter out reactions with missing user data and sort by createdAt (newest first)
  const sortedReactions = [...(reactions || [])]
    .filter(reaction => !!reaction.user)
    .sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
      // Fallback to id-based sorting if createdAt is missing
      return b.id - a.id
    })

  const groupedReactions = sortedReactions.reduce(
    (acc, reaction) => {
      const key = getReactionGroupKey(reaction)

      if (!acc[key]) {
        acc[key] = {
          reactions: [],
          emojiData: reaction.emojiData,
          reactionType: reaction.reactionType,
        }
      }
      acc[key].reactions.push(reaction)
      return acc
    },
    {} as Record<string, { reactions: BaseReaction[]; emojiData?: BaseReaction['emojiData']; reactionType: string }>
  )

  // Create tabs array without "all" tab
  const reactionTypes = Object.keys(groupedReactions)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex flex-col overflow-hidden bg-white dark:bg-zinc-950">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">Reactions</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue={reactionTypes[0] || ''} className="w-full flex flex-col flex-1">
          <div className="relative">
            <div className="overflow-x-auto scrollbar-hide">
              <TabsList className="h-16 inline-flex w-max bg-transparent p-0 gap-2 text-foreground">
                {reactionTypes.map(type => {
                  const hasCurrentUserReaction = groupedReactions[type].reactions.some(
                    reaction => reaction.user.id === user?.id
                  )

                  return (
                    <TabsTrigger
                      key={type}
                      value={type}
                      className="flex items-center gap-1.5 text-sm min-w-[90px] px-3 flex-shrink-0 rounded-md border border-transparent data-[state=active]:border-border data-[state=active]:bg-background data-[state=active]:shadow-sm"
                    >
                      <div className="flex items-center gap-1.5">
                        {renderReactionIcon(groupedReactions[type].emojiData, groupedReactions[type].reactionType, 'sm')}
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full ${
                            hasCurrentUserReaction
                              ? 'bg-blue-200 text-blue-800'
                              : 'border border-border bg-transparent text-muted-foreground'
                          }`}
                        >
                          {groupedReactions[type].reactions.length}
                        </span>
                      </div>
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>
          </div>

          <div className="flex-1 mt-5">
            <ScrollArea className="h-full">
              {reactionTypes.map(type => (
                <TabsContent
                  key={type}
                  value={type}
                  className="mt-0 overflow-y-auto overflow-x-hidden h-[36vh] max-h-[36vh]"
                >
                  {groupedReactions[type].reactions.map(reaction => (
                    <motion.div
                      key={`${reaction.id}-${reaction.user.id}-${type}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <ResolvedAvatarImage
                          avatar={reaction.user.avatar || (reaction.user.id === user?.id ? user?.avatar : null)}
                          alt={reaction.user.firstName}
                        />
                        <AvatarFallback>
                          {reaction.user.firstName}
                          {reaction.user.lastName}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm md:text-base">
                          {reaction.user.firstName || user?.firstName} {reaction.user.lastName || user?.lastName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xl md:text-2xl flex-shrink-0">
                          {renderReactionIcon(reaction.emojiData, reaction.reactionType)}
                        </div>
                        {onRemoveReaction && reaction.user.id === user?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                            onClick={() =>
                              messageId && onRemoveReaction(messageId, reaction.reactionType, reaction.emojiData)
                            }
                            title="Remove your reaction"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </TabsContent>
              ))}
            </ScrollArea>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
